import { useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import api from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Calculator, FileCheck2, ReceiptText, FileStack, AlertTriangle } from "lucide-react";
import { rupiah, tanggal, angka } from "@/lib/format";

export default function GenerateTagihan() {
  const { data: clients } = useFetch("/clients");
  const { data: projects } = useFetch("/projects");
  const { data: contracts } = useFetch("/contracts");
  const { data: pos } = useFetch("/purchase-orders");
  const navigate = useNavigate();

  const [sel, setSel] = useState({ client_id: "", project_id: "", contract_id: "", po_ids: [], billing_start: "2025-08-01", billing_end: "2025-08-31" });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const projOptions = (projects || []).filter((p) => p.client_id === sel.client_id);
  const conOptions = (contracts || []).filter((c) => c.project_id === sel.project_id);
  const poOptions = (pos || []).filter((p) => p.project_id === sel.project_id);

  const set = (k, v) => setSel((s) => ({ ...s, [k]: v }));
  const togglePO = (id) => setSel((s) => ({ ...s, po_ids: s.po_ids.includes(id) ? s.po_ids.filter((x) => x !== id) : [...s.po_ids, id] }));

  const runPreview = async () => {
    if (!sel.project_id || !sel.contract_id) { toast.error("Pilih proyek & kontrak"); return; }
    setLoading(true);
    try {
      const res = await api.post("/billing/preview", sel);
      setPreview(res.data);
      if (res.data.lines.length === 0) toast.info("Tidak ada transaksi pada periode ini");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghitung");
    } finally { setLoading(false); }
  };

  const create = async (mode) => {
    setCreating(true);
    try {
      const con = (contracts || []).find((c) => c.id === sel.contract_id);
      const payload = { ...sel, tax_percent: con?.tax_percent ?? 11, due_date: sel.billing_end,
        po_numbers: poOptions.filter((p) => sel.po_ids.includes(p.id)).map((p) => p.number).join(", ") || "-" };
      if (mode === "proforma") { const r = await api.post("/proformas", payload); toast.success(`Proforma ${r.data.number} dibuat`); navigate("/proformas"); }
      else if (mode === "invoice") { const r = await api.post("/invoices", payload); toast.success(`Draft Invoice ${r.data.number} dibuat`); navigate("/invoices"); }
      else { const r = await api.post("/extensions/generate", payload); toast.success(`Lampiran ${r.data.number} dibuat`); navigate("/proformas"); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal membuat dokumen");
    } finally { setCreating(false); }
  };

  return (
    <div>
      <PageHeader title="Generate Tagihan" subtitle="Hitung sewa dari surat jalan & pengembalian, lalu buat Proforma atau Invoice" />
      <Card className="p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Field label="Klien"><Select value={sel.client_id} onValueChange={(v) => setSel({ ...sel, client_id: v, project_id: "", contract_id: "", po_ids: [] })}><SelectTrigger data-testid="gen-client"><SelectValue placeholder="Klien" /></SelectTrigger><SelectContent>{(clients || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></Field>
          <Field label="Proyek"><Select value={sel.project_id} onValueChange={(v) => setSel({ ...sel, project_id: v, contract_id: "", po_ids: [] })}><SelectTrigger data-testid="gen-project"><SelectValue placeholder="Proyek" /></SelectTrigger><SelectContent>{projOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></Field>
          <Field label="Kontrak"><Select value={sel.contract_id} onValueChange={(v) => set("contract_id", v)}><SelectTrigger data-testid="gen-contract"><SelectValue placeholder="Kontrak" /></SelectTrigger><SelectContent>{conOptions.map((c) => <SelectItem key={c.id} value={c.id}>{c.number}</SelectItem>)}</SelectContent></Select></Field>
          <Field label="Mulai Periode"><Input type="date" data-testid="gen-start" value={sel.billing_start} onChange={(e) => set("billing_start", e.target.value)} /></Field>
          <Field label="Akhir Periode"><Input type="date" data-testid="gen-end" value={sel.billing_end} onChange={(e) => set("billing_end", e.target.value)} /></Field>
          <div className="flex items-end"><Button onClick={runPreview} disabled={loading} data-testid="gen-preview-btn" className="w-full bg-[#9333EA] hover:bg-[#7e22ce]"><Calculator className="h-4 w-4 mr-1" />{loading ? "Menghitung..." : "Hitung"}</Button></div>
        </div>
        {poOptions.length > 0 && (
          <div className="mt-4">
            <Label className="text-sm">Purchase Order (opsional — kosongkan untuk semua)</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {poOptions.map((p) => (
                <button key={p.id} onClick={() => togglePO(p.id)} data-testid={`gen-po-${p.id}`}
                  className={`px-3 py-1.5 rounded-md border text-sm transition-colors ${sel.po_ids.includes(p.id) ? "bg-[#9333EA] text-white border-[#9333EA]" : "hover:bg-white/5"}`}>
                  {p.number}
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>

      {preview && (
        <Card className="p-6">
          {preview.warnings?.length > 0 && (
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 space-y-1">
              {preview.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-amber-700"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />{w}</div>
              ))}
            </div>
          )}
          <Tabs defaultValue="detail">
            <TabsList>
              <TabsTrigger value="ringkas" data-testid="tab-ringkas">Ringkas</TabsTrigger>
              <TabsTrigger value="detail" data-testid="tab-detail">Detail per Rentang Sewa</TabsTrigger>
              <TabsTrigger value="sumber" data-testid="tab-sumber">Sumber Transaksi</TabsTrigger>
            </TabsList>
            <TabsContent value="ringkas">
              <SummaryView lines={preview.lines} />
            </TabsContent>
            <TabsContent value="detail">
              <PreviewTable lines={preview.lines} />
            </TabsContent>
            <TabsContent value="sumber">
              <SourceTable lines={preview.lines} />
            </TabsContent>
          </Tabs>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-6 border-t">
            <div className="text-sm">
              <span className="text-muted-foreground">Subtotal Perhitungan: </span>
              <span className="font-display text-xl font-bold tabular-nums">{rupiah(preview.subtotal)}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => create("extension")} disabled={creating} data-testid="gen-extension-btn"><FileStack className="h-4 w-4 mr-1" /> Lampiran Perpanjangan</Button>
              <Button variant="outline" onClick={() => create("proforma")} disabled={creating} data-testid="gen-proforma-btn"><FileCheck2 className="h-4 w-4 mr-1" /> Buat Proforma</Button>
              <Button onClick={() => create("invoice")} disabled={creating} data-testid="gen-invoice-btn" className="bg-[#9333EA] hover:bg-[#7e22ce]"><ReceiptText className="h-4 w-4 mr-1" /> Langsung Invoice</Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-sm">{label}</Label>{children}</div>;
}

function PreviewTable({ lines }) {
  return (
    <div className="rounded-md border overflow-x-auto mt-4 scrollbar-thin">
      <table className="w-full text-sm">
        <thead><tr className="bg-muted/40 border-b text-xs">
          {["Material", "Jenis", "Qty", "Mulai", "Akhir", "Hari", "Tarif", "Subtotal"].map((h) => <th key={h} className={`px-3 py-2 ${["Qty", "Hari", "Tarif", "Subtotal"].includes(h) ? "text-right" : "text-left"}`}>{h}</th>)}
        </tr></thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} className="border-b last:border-0 hover:bg-white/5">
              <td className="px-3 py-2 font-medium">{l.material_name}</td>
              <td className="px-3 py-2"><StatusBadge status={l.material_status} /></td>
              <td className="px-3 py-2 text-right tabular-nums">{angka(l.quantity)}</td>
              <td className="px-3 py-2">{tanggal(l.rental_start)}</td>
              <td className="px-3 py-2">{tanggal(l.rental_end)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{l.number_of_days}</td>
              <td className="px-3 py-2 text-right tabular-nums">{rupiah(l.rate)}</td>
              <td className="px-3 py-2 text-right tabular-nums font-medium">{rupiah(l.subtotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SummaryView({ lines }) {
  const byMat = {};
  lines.forEach((l) => {
    const m = byMat[l.material_id] || { name: l.material_name, qty: 0, subtotal: 0 };
    m.qty += l.quantity; m.subtotal += l.subtotal; byMat[l.material_id] = m;
  });
  return (
    <div className="rounded-md border overflow-x-auto mt-4">
      <table className="w-full text-sm">
        <thead><tr className="bg-muted/40 border-b text-xs"><th className="text-left px-3 py-2">Material</th><th className="text-right px-3 py-2">Total Qty</th><th className="text-right px-3 py-2">Subtotal</th></tr></thead>
        <tbody>{Object.values(byMat).map((m, i) => (
          <tr key={i} className="border-b last:border-0"><td className="px-3 py-2 font-medium">{m.name}</td><td className="px-3 py-2 text-right tabular-nums">{angka(m.qty)}</td><td className="px-3 py-2 text-right tabular-nums font-medium">{rupiah(m.subtotal)}</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function SourceTable({ lines }) {
  return (
    <div className="rounded-md border overflow-x-auto mt-4">
      <table className="w-full text-sm">
        <thead><tr className="bg-muted/40 border-b text-xs">{["Material", "Surat Jalan", "Tgl SJ", "Surat Pengembalian", "Tgl Kembali"].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
        <tbody>{lines.map((l, i) => (
          <tr key={i} className="border-b last:border-0"><td className="px-3 py-2">{l.material_name}</td><td className="px-3 py-2">{l.dn_number || "-"}</td><td className="px-3 py-2">{tanggal(l.dn_date)}</td><td className="px-3 py-2">{l.rn_number || "-"}</td><td className="px-3 py-2">{l.return_date ? tanggal(l.return_date) : "-"}</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}
